import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Copy, Upload, AlertTriangle, CheckCircle2, Loader2, School } from 'lucide-react';
import { toast } from 'sonner';

const PRETTY = {
  children: 'students', consent_records: 'consent records', mentor_profiles: 'mentor profiles',
  mentor_approval_records: 'mentor approvals', mentor_declaration_records: 'safeguarding declarations',
  mentor_asks: 'mentor requests', mentor_recognitions: 'mentor recognitions', sessions: 'sessions',
  bookings: 'experiences', internship_programs: 'internship programmes',
  internship_opportunities: 'internship offers', internship_invitations: 'partner invitations',
  internship_applications: 'internship applications', internship_proposals: 'internship proposals',
  internship_attendance: 'attendance days', school_registrations: 'registration history',
};

export function CloneDemoSchoolDialog({ sourceSchoolId = 'DM0001', onClose, onDone }) {
  const [name, setName] = useState('');
  const [prefix, setPrefix] = useState('');
  const [logo, setLogo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [working, setWorking] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await api.get('/admin/schools/clone-preview', {
        params: { source_school_id: sourceSchoolId, prefix: prefix.trim().toLowerCase() },
      });
      setPreview(res.data);
    } catch {
      setPreview(null);
    }
  }, [sourceSchoolId, prefix]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const prefixProblem = prefix && preview && (
    preview.prefix_valid === false
      ? 'Use 2–12 lowercase letters or digits, no spaces or dots'
      : preview.prefix_available === false ? 'Another school already uses this prefix' : '');

  const ready = name.trim() && logo && prefix.trim() && !prefixProblem;

  const runClone = async () => {
    setWorking(true);
    setError('');
    try {
      const form = new FormData();
      form.append('source_school_id', sourceSchoolId);
      form.append('new_school_name', name.trim());
      form.append('email_prefix', prefix.trim().toLowerCase());
      form.append('confirm', 'true');
      form.append('logo', logo);
      const res = await api.post('/admin/schools/clone', form,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
      setConfirming(false);
      toast.success(`${res.data.school_name} created as ${res.data.school_id}`);
      onDone?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'The clone could not be completed');
      setConfirming(false);
    }
    setWorking(false);
  };

  const copyAccounts = () => {
    const text = (result.accounts || [])
      .map(a => `${a.role}${a.is_mentor ? ' / mentor' : ''}\t${a.full_name}\t${a.email}\t${a.password}`)
      .join('\n');
    navigator.clipboard.writeText(text);
    toast.success('Demo logins copied');
  };

  if (result) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="rounded-2xl sm:max-w-2xl" data-testid="clone-result-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              {result.school_name} is ready
            </DialogTitle>
            <DialogDescription>
              {result.school_id} · pilot school on DEMO email · invite code {result.invite_code} ·{' '}
              {result.total_records} records copied, {result.certificates?.sessions || 0} experience,{' '}
              {result.certificates?.internships || 0} internship and{' '}
              {result.certificates?.recognitions || 0} recognition certificates regenerated.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
            These passwords are shown once and stored only as hashes. Copy them now — you can always
            issue a fresh one per account from Manage school.
          </div>
          <div className="max-h-72 overflow-y-auto rounded-xl border border-gray-200"
               data-testid="clone-credentials">
            <table className="w-full text-xs">
              <tbody>
                {(result.accounts || []).map(a => (
                  <tr key={a.id} className="border-b border-gray-50 last:border-0">
                    <td className="py-2 px-3">
                      <p className="font-semibold text-[#1F2937]">{a.full_name}</p>
                      <p className="text-[10px] uppercase tracking-wide text-gray-400">
                        {a.role}{a.is_mentor ? ' · mentor' : ''}
                      </p>
                    </td>
                    <td className="py-2 px-3 font-mono text-[11px] text-gray-600">{a.email}</td>
                    <td className="py-2 px-3 font-mono text-[11px] text-[#F97D6A]">{a.password}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={copyAccounts} className="rounded-xl"
                    data-testid="copy-clone-credentials">
              <Copy className="h-4 w-4 mr-1" /> Copy all
            </Button>
            <Button onClick={onClose} className="bg-[#F97D6A] hover:bg-[#F06651] rounded-xl"
                    data-testid="close-clone-result">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl sm:max-w-xl" data-testid="clone-school-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] flex items-center gap-2">
            <School className="h-5 w-5 text-[#F97D6A]" /> Create a pilot school
          </DialogTitle>
          <DialogDescription>
            Makes an independent pilot copy of {preview?.source_school_name || sourceSchoolId} — every
            account, student, experience, internship and certificate. It gets its own permanent PL
            number and starts with all email going only to the monitoring inbox. A live school is
            never created this way; that only happens through the official registration flow.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs font-semibold text-gray-600">New school name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="The Kalyani School" className="rounded-xl mt-1"
                   data-testid="clone-name-input" />
          </div>

          <div>
            <Label className="text-xs font-semibold text-gray-600">Email prefix</Label>
            <Input value={prefix} onChange={(e) => setPrefix(e.target.value.toLowerCase())}
                   placeholder="tks" className="rounded-xl mt-1 font-mono"
                   data-testid="clone-prefix-input" />
            {prefixProblem
              ? <p className="text-xs text-red-500 mt-1" data-testid="clone-prefix-error">{prefixProblem}</p>
              : <p className="text-xs text-gray-400 mt-1">
                  Keeps this school's logins unique and separate from every other school.
                </p>}
          </div>

          <div>
            <Label className="text-xs font-semibold text-gray-600">School logo</Label>
            <label className="mt-1 flex items-center gap-3 rounded-xl border border-dashed border-gray-300 px-4 py-3 cursor-pointer hover:border-[#F97D6A] transition-colors">
              <Upload className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-500">
                {logo ? logo.name : 'Choose the crest shown on certificates'}
              </span>
              <input type="file" accept="image/*" className="hidden"
                     onChange={(e) => setLogo(e.target.files[0] || null)}
                     data-testid="clone-logo-input" />
            </label>
          </div>

          {preview && (
            <div className="rounded-xl bg-[#FFF0ED] border border-[#FBD9D2] px-4 py-3 space-y-2"
                 data-testid="clone-preview">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">New school ID</span>
                <Badge className="bg-white text-[#F97D6A] border-0 font-mono">
                  {preview.new_school_id}
                </Badge>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Records to copy</span>
                <span className="font-semibold text-[#1F2937]" data-testid="clone-record-total">
                  {preview.total_records}
                </span>
              </div>
              <p className="text-[11px] text-gray-500 leading-relaxed">
                {Object.entries(preview.record_counts || {})
                  .map(([k, v]) => `${v} ${PRETTY[k] || k}`).join(' · ')}
              </p>
              {(preview.samples || []).length === 0 && prefix && !prefixProblem && (
                <p className="text-[11px] text-gray-500">Logins will use the “name.{prefix}@…” pattern.</p>
              )}
              {prefix && !prefixProblem && (
                <div className="pt-1 space-y-0.5" data-testid="clone-sample-logins">
                  {(preview.samples || []).slice(0, 4).map(s => (
                    <p key={s.email} className="text-[11px] font-mono text-gray-600">{s.email}</p>
                  ))}
                  {(preview.samples || []).length > 4 && (
                    <p className="text-[11px] text-gray-400">
                      + {preview.samples.length - 4} more accounts
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
                 data-testid="clone-error">{error}</div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
          <Button disabled={!ready || working} onClick={() => setConfirming(true)}
                  className="bg-[#F97D6A] hover:bg-[#F06651] rounded-xl"
                  data-testid="start-clone-btn">
            Review and clone
          </Button>
        </DialogFooter>

        {confirming && (
          <Dialog open onOpenChange={() => setConfirming(false)}>
            <DialogContent className="rounded-2xl sm:max-w-md" data-testid="clone-confirm-dialog">
              <DialogHeader>
                <DialogTitle className="font-['Outfit'] flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" /> Create {name.trim()}?
                </DialogTitle>
                <DialogDescription>
                  {preview?.total_records} records and {(preview?.accounts?.coordinators || 0)
                  + (preview?.accounts?.parents || 0) + (preview?.accounts?.partners || 0)} accounts
                  will be copied into {preview?.new_school_id}, a pilot school, with all email still
                  going only to the monitoring inbox. Nothing is sent to anyone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirming(false)}
                        className="rounded-xl">Back</Button>
                <Button onClick={runClone} disabled={working}
                        className="bg-[#F97D6A] hover:bg-[#F06651] rounded-xl"
                        data-testid="confirm-clone-btn">
                  {working ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Cloning…</>
                           : 'Yes, clone it'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default CloneDemoSchoolDialog;
